import { httpClient } from "./client";
import type { CheckinApi } from "./interfaces";
import type { UUID } from "../types";
import type { BookingPassState, CheckinPassResponse } from "../types/generated";

/**
 * Check-in pass spine (player-self).
 *
 * Mints the short-lived QR pass a player shows at the desk; staff scan it and
 * redeem it via the business app (POST /facilities/{id}/checkins/redeem — NOT
 * exposed here; the player UI is purely the presenter). The token is a JWT
 * (HS256, signed with CHECKIN_SECRET — separate from Firebase) with a 5-minute
 * server TTL and is single-use (the `jti` is recorded on redemption).
 *
 * Frictionless drop-in (2026-07-02): the queue-entry / assignment passes were
 * removed — a drop-in assignment is on court immediately, so there is nothing to
 * scan there. The BOOKING pass remains.
 *
 * Staff QR-validation gate (2026-07-04): the DROP-IN pass is re-added. When a
 * facility runs staff_check_in, paying leaves the player in `pending_validation`;
 * they show this pass and staff scan it to validate them onto the floor. The pass
 * is minted for the player's CURRENT (active OR pending) session at the facility.
 */
export const checkinApi: CheckinApi = {
  // Pass for a confirmed court booking — the host OR any accepted guest on the
  // roster may mint it. 404 if the booking is gone, 403 for a non-participant,
  // 409 if the booking is not confirmed; the httpClient rejects on each (never
  // a fabricated pass). Mirrors the dropin pass method one segment over
  // (/checkin/bookings/{id}/pass).
  getBookingPass: (bookingId: UUID) =>
    httpClient.get<CheckinPassResponse>(
      `/checkin/bookings/${encodeURIComponent(bookingId)}/pass`,
    ),
  // Pass for the caller's CURRENT drop-in session at a facility (active OR
  // pending_validation). Staff scan it to validate a pending player onto the
  // floor. 404 when there is no session to mint against, 403 for another
  // player's session; the httpClient rejects on each (never a fabricated pass).
  // Mirrors getBookingPass one segment over (/checkin/dropin/{facilityId}/pass).
  getDropinPass: (facilityId: UUID) =>
    httpClient.get<CheckinPassResponse>(
      `/checkin/dropin/${encodeURIComponent(facilityId)}/pass`,
    ),
  // Owner rule (#2691): no new QR once used or ended. The Check in hub reads
  // this so it shows "Checked in", "Session ended" or "Cancelled" instead of
  // "Show code"; the pass endpoints refuse with the same 409 codes.
  listBookingPassStates: (facilityId: UUID) =>
    httpClient.get<BookingPassState[]>(
      `/checkin/bookings/pass-states?facility_id=${encodeURIComponent(facilityId)}`,
    ),
};
